"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import ChevronRight from "@/components/ui/chevron-right";
import ChevronLeft from "@/components/ui/chevron-left";

import { cn } from "@/lib/utils";
import { Button } from "./button";

import { StrapiDescription } from "@/components/strapi/StrapiDescription";
import type { StrapiRichTextBlock } from "@/lib/strapi";

export type Testimonial = {
  description: string | StrapiRichTextBlock[] | null | undefined;
  name: string;
  designation: string;
  src: string;
  ctaLabel?: string;
  ctaHref?: string;
  productSlug?: string;
  productSize?: string;
};

export interface AnimatedTestimonialsProps {
  testimonials: Testimonial[];
  autoplay?: boolean;
  className?: string;
  autoplayDelayMs?: number;
  showNavigation?: boolean;
  navigationClassName?: string;
}

export type AnimatedTestimonialsHandle = {
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
};

type MediaKind = "image" | "video";

function inferMediaKindFromUrl(url: string): MediaKind {
  const normalized = url.split("?")[0]?.toLowerCase() ?? "";
  if (/\.(mp4|webm|ogg|mov|m4v)$/i.test(normalized)) return "video";
  return "image";
}

export const AnimatedTestimonials = React.forwardRef<AnimatedTestimonialsHandle, AnimatedTestimonialsProps>(
  function AnimatedTestimonials(
    {
      testimonials,
      autoplay = false,
      autoplayDelayMs = 5000,
      className,
      showNavigation = true,
      navigationClassName,
    },
    ref,
  ) {
  const [active, setActive] = React.useState(0);
  const [direction, setDirection] = React.useState<1 | -1>(1);
  const autoplayRef = React.useRef<number | null>(null);
  const swipeRef = React.useRef<{
    pointerId: number | null;
    startX: number;
    startY: number;
    startAt: number;
    lastActionAt: number;
  }>({
    pointerId: null,
    startX: 0,
    startY: 0,
    startAt: 0,
    lastActionAt: 0,
  });
  const slideVariants = React.useMemo(
    () => ({
      enter: (dir: 1 | -1) => ({
        x: dir > 0 ? 32 : -32,
        opacity: 0,
      }),
      center: {
        x: 0,
        opacity: 1,
      },
      exit: (dir: 1 | -1) => ({
        x: dir > 0 ? -32 : 32,
        opacity: 0,
      }),
    }),
    [],
  );

  const goNext = React.useCallback(() => {
    setDirection(1);
    setActive((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const goPrev = React.useCallback(() => {
    setDirection(-1);
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const restartAutoplay = React.useCallback(() => {
    if (!autoplay) return;
    if (autoplayRef.current) window.clearInterval(autoplayRef.current);
    autoplayRef.current = window.setInterval(goNext, autoplayDelayMs);
  }, [autoplay, autoplayDelayMs, goNext]);

  const handlePrev = React.useCallback(() => {
    goPrev();
    restartAutoplay();
  }, [goPrev, restartAutoplay]);

  const handleNext = React.useCallback(() => {
    goNext();
    restartAutoplay();
  }, [goNext, restartAutoplay]);

  const goTo = React.useCallback(
    (index: number) => {
      const count = testimonials.length;
      if (count <= 1) return;
      const nextIndex = ((index % count) + count) % count;
      setDirection(nextIndex >= active ? 1 : -1);
      setActive(nextIndex);
      restartAutoplay();
    },
    [active, restartAutoplay, testimonials.length],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      next: handleNext,
      prev: handlePrev,
      goTo,
    }),
    [goTo, handleNext, handlePrev],
  );

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      if (testimonials.length <= 1) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if ((event.target as HTMLElement | null)?.closest?.('[data-swipe-ignore="true"]')) return;

      swipeRef.current.pointerId = event.pointerId;
      swipeRef.current.startX = event.clientX;
      swipeRef.current.startY = event.clientY;
      swipeRef.current.startAt = Date.now();
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [testimonials.length],
  );

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent) => {
      if (swipeRef.current.pointerId === null) return;
      if (event.pointerId !== swipeRef.current.pointerId) return;
      swipeRef.current.pointerId = null;

      const now = Date.now();
      if (now - swipeRef.current.lastActionAt < 350) return;

      const dx = event.clientX - swipeRef.current.startX;
      const dy = event.clientY - swipeRef.current.startY;
      const dt = now - swipeRef.current.startAt;

      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      const isHorizontal = absX > absY * 1.2;
      const passedThreshold = absX >= 50;
      const isReasonableTime = dt <= 900;

      if (isHorizontal && passedThreshold && isReasonableTime) {
        swipeRef.current.lastActionAt = now;
        if (dx < 0) goNext();
        else goPrev();
        restartAutoplay();
      }
    },
    [goNext, goPrev, restartAutoplay],
  );

  React.useEffect(() => {
    if (!autoplay || testimonials.length <= 1) return;
    if (autoplayRef.current) window.clearInterval(autoplayRef.current);
    autoplayRef.current = window.setInterval(goNext, autoplayDelayMs);
    return () => {
      if (autoplayRef.current) window.clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    };
  }, [autoplay, autoplayDelayMs, goNext, testimonials.length]);

  if (!testimonials.length) return null;

  const toSlug = (value: string) =>
    value
      .toLowerCase()
      .trim()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const parseNameForProduct = (name: string) => {
    const sizeMatch = name.match(/\b\d+\s*ml\b/i);
    const sizeRaw = sizeMatch?.[0];
    const size = sizeRaw ? sizeRaw.replace(/\s+/g, "").toLowerCase() : undefined;
    const cleanedName = sizeRaw ? name.replace(sizeRaw, "").replace(/\s{2,}/g, " ").trim() : name.trim();
    return { slug: toSlug(cleanedName), size };
  };

  const activeTestimonial = testimonials[active];
  const derived = parseNameForProduct(activeTestimonial.name);
  const resolvedSlug = activeTestimonial.productSlug ?? derived.slug;
  const resolvedSize = activeTestimonial.productSize ?? derived.size;
  const mediaKind = inferMediaKindFromUrl(activeTestimonial.src);
  const ctaHref =
    activeTestimonial.ctaHref ??
    (resolvedSlug
      ? `/products/${resolvedSlug}${resolvedSize ? `?size=${encodeURIComponent(resolvedSize)}` : ""}`
      : "#");

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-3xl bg-primary p-2 text-background sm:p-6 md:h-[648px] touch-pan-y",
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* <div className="pointer-events-none absolute inset-3 rounded-3xl" /> */}

      <div className="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-4">
        <div className="h-[350px] sm:h-[595px] aspect-video w-xs pt-6 sm:pt-0">
          {mediaKind === "video" ? (
              <video
                src={activeTestimonial.src}
                className="h-full w-full object-cover rounded-xl"
                muted
                playsInline
                autoPlay
                loop
                preload="metadata"
                tabIndex={-1}
                disablePictureInPicture
                controlsList="nodownload noplaybackrate noremoteplayback"
              />
            ) : (
              <Image
                src={activeTestimonial.src}
                alt={activeTestimonial.name}
                width={480}
                height={595}
                quality={95}
                draggable={false}
                priority
                className="h-[350px] sm:h-[595px] rounded-xl object-cover"
              />
            )}
        </div>
        <div className="flex flex-col items-center justify-between! gap-3 py-1 text-center sm:gap-4 sm:py-24">
          <div className="text-[clamp(12px,1.1vw,18px)] text-center font-normal text-white/90">
            Popular Products
          </div>

          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={active}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="flex h-full min-h-[340px]! flex-col items-center justify-center"
            >
              <h3 className="mt-2 text-[clamp(18px,3.1vw,40px)] font-semibold leading-[1.1] tracking-tight">
                {activeTestimonial.name}
              </h3>

              <div className="prose prose-sm mt-3 max-w-none text-[clamp(13px,1.5vw,18px)] leading-relaxed text-white/75">
                <StrapiDescription data={activeTestimonial.description} />
              </div>

              {activeTestimonial.ctaLabel ? (
                <div className="mt-4 flex justify-center sm:mt-6">
                  <Link
                    href={ctaHref}
                    data-swipe-ignore="true"
                    className="rounded-full bg-white px-6 py-2 text-[clamp(12px,1.1vw,16px)] font-medium text-primary"
                  >
                    {activeTestimonial.ctaLabel}
                  </Link>
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>

          {testimonials.length > 1 ? (
            <div
              className={cn(
                "mt-6 flex items-center justify-center gap-4 sm:mt-8 md:mt-0",
                navigationClassName,
              )}
              data-swipe-ignore="true"
            >
              {showNavigation ? (
                <Button
                  type="button"
                  onClick={handlePrev}
                  aria-label="Previous"
                  className="group/button grid h-9 w-9 place-items-center rounded-full bg-white ring-1 ring-white/15 transition hover:bg-white/90 sm:h-10 sm:w-10 cursor-pointer"
                >
                  <ChevronLeft className="h-5 w-5 text-primary transition-transform duration-300 group-hover/button:-rotate-12" />
                </Button>
              ) : null}

              {showNavigation ? (
                <div className="flex items-center justify-center gap-2">
                  {testimonials.map((_, index) => {
                    const selected = index === active;
                    return (
                      <button
                        key={`dot-${index}`}
                        type="button"
                        onClick={() => goTo(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={selected ? "true" : undefined}
                        className={cn(
                          "h-2 w-2 rounded-full transition cursor-pointer",
                          selected ? "bg-white" : "bg-white/35 hover:bg-white/55",
                        )}
                      />
                    );
                  })}
                </div>
              ) : null}

              {showNavigation ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  aria-label="Next"
                  className="group/button grid h-9 w-9 place-items-center rounded-full bg-white ring-1 ring-white/15 transition hover:bg-white/90 sm:h-10 sm:w-10 cursor-pointer"
                >
                  <ChevronRight className="h-5 w-5 text-primary transition-transform duration-300 group-hover/button:rotate-12" />
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
  },
);
